/**
 * StepGradingMatrix — per-task × per-step reward heatmap plus per-model
 * task-score columns, from scores.jsonl step_rewards. Mirrors the Mercor
 * console's "Step-by-step grading" view: each cell is the mean reward at
 * that step; the right-hand block is each model's task score.
 *
 * Rows sort by all-model mean descending; the filter narrows by slug.
 * Returns null when no row carries step rewards (e.g. GDPVal).
 */

import { useState } from 'react'
import { useNavigate } from 'react-router-dom'

import { formatScore } from '@/components/evals/format'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import type { BenchmarkScore } from '@/api/generated/model'

interface StepGradingMatrixProps {
  scores: BenchmarkScore[]
  taskPath: (taskSlug: string) => string
}

interface MatrixRow {
  taskSlug: string
  stepMeans: (number | null)[]
  modelScores: Map<string, number>
  allMean: number
}

/** Whisper tint scaled by score — same literal ramp as VariantMatrix. */
function cellStyle(score: number): React.CSSProperties {
  return { backgroundColor: `hsl(${(score * 130).toFixed(0)} 40% 58% / 0.35)` }
}

function buildRows(scores: BenchmarkScore[]): { rows: MatrixRow[]; maxSteps: number; models: string[] } {
  const byTask = new Map<string, BenchmarkScore[]>()
  for (const score of scores) {
    const rows = byTask.get(score.task_slug)
    if (rows === undefined) {
      byTask.set(score.task_slug, [score])
    } else {
      rows.push(score)
    }
  }
  const models = Array.from(new Set(scores.map((s) => s.model))).sort()

  const rows: MatrixRow[] = []
  let maxSteps = 0
  for (const [taskSlug, taskScores] of byTask) {
    const stepped = taskScores.filter((s) => s.step_rewards.length > 0)
    if (stepped.length === 0) continue
    const stepCount = Math.max(...stepped.map((s) => s.step_rewards.length))
    maxSteps = Math.max(maxSteps, stepCount)
    const stepMeans: (number | null)[] = []
    for (let step = 0; step < stepCount; step++) {
      const values = stepped
        .map((s) => s.step_rewards[step])
        .filter((value): value is number => value !== undefined)
      stepMeans.push(
        values.length > 0 ? values.reduce((sum, v) => sum + v, 0) / values.length : null
      )
    }
    rows.push({
      taskSlug,
      stepMeans,
      modelScores: new Map(taskScores.map((s) => [s.model, s.mean_score])),
      allMean:
        taskScores.reduce((sum, s) => sum + s.mean_score, 0) / taskScores.length,
    })
  }
  rows.sort((a, b) => b.allMean - a.allMean)
  return { rows, maxSteps, models }
}

export function StepGradingMatrix({ scores, taskPath }: StepGradingMatrixProps): React.JSX.Element | null {
  const navigate = useNavigate()
  const [filter, setFilter] = useState('')
  const { rows, maxSteps, models } = buildRows(scores)
  if (rows.length === 0) return null

  const needle = filter.trim().toLowerCase()
  const visible = needle === '' ? rows : rows.filter((row) => row.taskSlug.toLowerCase().includes(needle))

  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-sm font-medium text-muted-foreground">
          Step-by-step grading: mean reward at each trajectory step
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        <Input
          value={filter}
          onChange={(event) => {
            setFilter(event.target.value)
          }}
          placeholder="Filter by task slug…"
          aria-label="Filter tasks in the step grading matrix"
          className="max-w-xs"
        />
        <div className="overflow-x-auto">
          <div
            className="grid gap-0.5 text-xs"
            style={{
              gridTemplateColumns: `max-content repeat(${maxSteps.toString()}, minmax(2.75rem, 1fr)) 1rem repeat(${models.length.toString()}, minmax(3.5rem, 1fr))`,
            }}
          >
            <span aria-hidden="true" />
            {Array.from({ length: maxSteps }, (_, index) => (
              <span key={index} className="pb-1 text-center font-mono text-muted-foreground">
                {(index + 1).toString().padStart(2, '0')}
              </span>
            ))}
            <span aria-hidden="true" />
            {models.map((model) => (
              <span key={model} className="truncate pb-1 text-center font-mono text-muted-foreground" title={model}>
                {model}
              </span>
            ))}

            {visible.map((row) => (
              <div key={row.taskSlug} className="contents">
                <button
                  type="button"
                  onClick={() => {
                    navigate(taskPath(row.taskSlug))
                  }}
                  className="truncate pr-3 text-left font-mono text-foreground hover:text-primary hover:underline"
                >
                  {row.taskSlug}
                </button>
                {Array.from({ length: maxSteps }, (_, step) => {
                  const value = row.stepMeans[step]
                  if (value === undefined || value === null) {
                    return <span key={step} aria-hidden="true" />
                  }
                  return (
                    <span
                      key={step}
                      style={cellStyle(value)}
                      className="flex min-h-7 items-center justify-center rounded tabular-nums"
                    >
                      {value.toFixed(2)}
                    </span>
                  )
                })}
                <span aria-hidden="true" />
                {models.map((model) => {
                  const value = row.modelScores.get(model)
                  if (value === undefined) {
                    return (
                      <span
                        key={model}
                        className="flex min-h-7 items-center justify-center rounded border border-dashed border-border text-muted-foreground"
                      >
                        —
                      </span>
                    )
                  }
                  return (
                    <span
                      key={model}
                      style={cellStyle(value)}
                      className="flex min-h-7 items-center justify-center rounded font-medium tabular-nums"
                    >
                      {formatScore(value)}
                    </span>
                  )
                })}
              </div>
            ))}
          </div>
        </div>
      </CardContent>
    </Card>
  )
}
